import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton, LinearProgress, Stack, Typography, useTheme } from '@mui/material';

import { Strings } from '@app/constants';
import { GoBackButton, Iconify, RecipeCard } from '@app/components';
import { useRequestState } from '@app/hooks';
import { getSelectedIngredients } from '@app/modules/select-ingredients/selectors';
import { selectedIngredients } from '@app/modules/select-ingredients/slice';
import { getSearchedItems } from '@app/modules/search/selectors';
import useResponsive from '@app/hooks/use-responsive';
import { PATH_DASHBOARD } from '@app/routes/paths';

const IngredientBasedRecipeScreen = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const navigation = useNavigate();

  const isMobile = useResponsive('down', 'md');
  const isTab = useResponsive('down', 'lg');

  const { mealType, dishType, selectedItems } = state;
  const { data: searchedResults } = useSelector(getSearchedItems);

  const { loading } = useRequestState({
    stateSelector: getSelectedIngredients,
    errorShown: true,
  });

  const [newIngredients, setNewIngredients] = useState(selectedItems);

  useEffect(() => {
    if (newIngredients?.length !== 0) {
      const labels = newIngredients.map((item: any) => item.label.toLowerCase());

      dispatch(
        selectedIngredients({
          mealType: mealType.toLowerCase(),
          dishType,
          ingredients: labels,
        })
      );
    }
  }, [newIngredients]);

  function renderIngredientsList() {
    return (
      <Stack
        style={{
          flexDirection: 'row',
          gap: 15,
          overflowX: 'auto',
          width: '100%',
          scrollbarWidth: 'none',
          paddingBottom: 5,
        }}
      >
        {newIngredients?.map((item: any, index: number) => {
          return (
            <Stack
              key={index}
              sx={{
                bgcolor: theme.palette.secondary.light,
                borderRadius: '10px',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '40px',
              }}
            >
              <Typography variant="subtitle2" px={2}>
                {item.label}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  const newList = newIngredients?.filter((data: any) => data?.id !== item?.id);
                  setNewIngredients(newList);
                }}
              >
                <Iconify icon="solar:minus-circle-bold" color={'white'} boxSizing={'inherit'} />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>
    );
  }

  const RenderItem = ({ item }: any, key: number) => {
    return (
      <Stack sx={{ borderRadius: '10px', width: isMobile ? '100%' : isTab ? 400 : 550 }}>
        <RecipeCard
          key={key}
          image={item?.image}
          dishName={item.label}
          cookingTime={`${item.totalTime} min`}
          ratings={item?.ratings || 'No Rating Yet'}
          description={item.label || item.description}
          id={item?.uri}
          onClick={() => navigation(PATH_DASHBOARD.recipeDetailsScreen, { state: item })}
        />
      </Stack>
    );
  };

  return (
    <Stack>
      <Typography variant="h3" color={theme.palette.primary.light}>
        <GoBackButton sx={{ mb: 1 }} /> {mealType}
      </Typography>
      <Typography variant="subtitle2" color="textDisabled">
        {Strings.selectedIngredients.yourChoice}
      </Typography>
      {renderIngredientsList()}
      {loading ? (
        <LinearProgress sx={{ mt: 10 }} />
      ) : (
        <Stack
          style={{
            flexDirection: 'row',
            gap: 15,
            width: isMobile ? '' : '91%',
            flexWrap: 'wrap',
            marginTop: 10,
            alignSelf: 'center',
          }}
        >
          {searchedResults?.map((item: any, index: number) => (
            <RenderItem key={index} item={item} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export { IngredientBasedRecipeScreen };
